import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { CompanyInterface } from './queryFn';
import queryFn from './queryFn';

interface REIT {
  NAV: number;
  NAVCount: number;
}

interface CompanyDetails {
  name: string;
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  primaryTradingItemCurrencyISO: string;
  primaryTradingItemCurrencySymbol: string;
  tradingItemCurrencyISO: string;
  tradingItemCurrencySymbol: string;
  primaryListingExchangeSymbol: string;
  primaryListingTickerSymbol: string;
  lastEarningsUpdateAnnual: number;
  analystCount: number;
  analystFcfCountAverage: number;
  companyShortName: string;
  exchangeSymbol: string;
  tickerSymbol: string;
  exchangeCountryISO: string;
  lastSharePrice: number;
  industrySecondaryName: string;
  relativeListingSharesOutstanding: number;
  model: string;
  unleveredBeta: number;
  de: number;
  taxRate: number;
  equityPremium: number;
  riskFreeRate: number;
  leveredBetaActual: number;
  leveredBeta: number;
  costOfEquity: number;
  adrPerShare: number;
  pv5y: number;
  terminalValue: number;
  pvtv: number;
  npv: number;
  npvPerShare: number;
  npvPerShareReportedCurrency?: number;
  intrinsicDiscount: number;
  REIT?: REIT;
  dividendDiscount?: {
    DDMGrowth: number;
    DDMSource: string;
    DPS: number;
    payout: number;
    ROE: number;
    expectedGrowth: number;
  };
  twoStagefcf?: {
    affoEstimates?: number;
    growthCAGR5y: number;
    scale: string;
    scaleNumeric: number;
    sharesOutstanding: number;
    fcfsource?: string;
    fcfData: { date: number; value: number }[];
    fcfCount: { date: number; value: number }[];
    fcfLtm: number;
    firstStage: {
      year: number;
      data: number;
      source: string;
      discounted: number;
    }[];
  };
  excessReturns?: {
    stableBookValue: number;
    returnOnEquityAvg: number;
    stableEPS: number;
    stableEPSSource: string;
    stableBookValueSource: string;
    bookValue: number;
    equityCost: number;
    excessReturn: number;
  };
}

function keyValueToList(table: null | Record<string, number>) {
  if (!table) return [];
  return Object.entries(table).map(([key, value]) => ({
    date: Number(key),
    value,
  }));
}

const getPrimaryListingSymbols = (
  listings: CompanyInterface['data']['listings']
): [string, string] => {
  const primaryListing = Object.values(listings).filter(
    (listing) => listing.primary_security && listing.primary_trading_item
  );
  if (primaryListing[0])
    return [primaryListing[0].exchange_symbol, primaryListing[0].ticker_symbol];
  return ['', ''];
};

export default function useCompany(
  companyId: string
): UseQueryResult<CompanyDetails> {
  return useQuery({
    queryKey: ['COMPANY', companyId],
    queryFn,
    staleTime: 3600000,
    retry: 1,
    refetchOnMount: false,
    select: (company): CompanyDetails => {
      const [primaryListingExchangeSymbol, primaryListingTickerSymbol] =
        getPrimaryListingSymbols(company.data.listings);
      const { data, analysisValue, info, analysisPast, analysisMisc } = company;
      const { currencyInfo, marketCap } = data;
      const { intrinsicValue } = analysisValue;
      const { excess_returns, two_stage_fcf, REIT, dividend_discount } =
        intrinsicValue;

      return {
        primaryListingExchangeSymbol,
        primaryListingTickerSymbol,
        name: company.name,
        uniqueSymbol: company.uniqueSymbol,
        reportingCurrencyISO: currencyInfo.reporting_currency_iso,
        reportingCurrencySymbol: currencyInfo.reporting_currency_symbol,
        primaryTradingItemCurrencyISO:
          currencyInfo.primary_trading_item_currency_iso,
        primaryTradingItemCurrencySymbol:
          currencyInfo.primary_trading_item_currency_symbol,
        tradingItemCurrencyISO: currencyInfo.trading_item_currency_iso,
        tradingItemCurrencySymbol: currencyInfo.trading_item_currency_symbol,
        lastEarningsUpdateAnnual: analysisPast.lastEarningsUpdateAnnual,
        analystCount: analysisMisc.analystCount,
        analystFcfCountAverage: analysisMisc.analystFcfCountAverage,
        companyShortName: company.name,
        exchangeSymbol: company.exchangeSymbol,
        tickerSymbol: company.tickerSymbol,
        exchangeCountryISO: company.exchangeCountryISO,
        lastSharePrice: analysisValue.lastSharePrice,
        industrySecondaryName: info.secondaryIndustryName,
        relativeListingSharesOutstanding:
          marketCap.relative_listing_shares_outstanding,
        model: intrinsicValue.model,
        unleveredBeta: intrinsicValue.unlevered_beta,
        de: intrinsicValue.de,
        taxRate: intrinsicValue.tax_rate,
        equityPremium: intrinsicValue.equity_premium,
        riskFreeRate: intrinsicValue.risk_free_rate,
        leveredBetaActual: intrinsicValue.levered_beta_actual,
        leveredBeta: intrinsicValue.levered_beta,
        costOfEquity: intrinsicValue.cost_of_equity,
        adrPerShare: intrinsicValue.adr_per_share,
        pv5y: intrinsicValue.pv_5y,
        terminalValue: intrinsicValue.terminal_value,
        pvtv: intrinsicValue.pvtv,
        npv: intrinsicValue.npv,
        npvPerShare: analysisValue.npvPerShare,
        npvPerShareReportedCurrency:
          intrinsicValue.npv_per_share_reported_currency,
        intrinsicDiscount: analysisValue.intrinsicDiscount,
        twoStagefcf:
          two_stage_fcf !== null
            ? {
                affoEstimates: two_stage_fcf.affo_estimates,
                growthCAGR5y: two_stage_fcf.growth_cagr_5y,
                scale: two_stage_fcf.scale,
                scaleNumeric: two_stage_fcf.scale_numeric,
                sharesOutstanding: two_stage_fcf.shares_outstanding,
                fcfsource: undefined,
                fcfData: keyValueToList(two_stage_fcf.fcf_data),
                fcfCount: keyValueToList(two_stage_fcf.fcf_count),
                fcfLtm: two_stage_fcf.fcf_ltm,
                firstStage: Object.entries(two_stage_fcf.first_stage || {}).map(
                  ([key, value]) => ({
                    year: parseInt(key),
                    data: value.data,
                    source: value.source,
                    discounted: value.discounted,
                  })
                ),
              }
            : undefined,
        REIT:
          REIT !== null
            ? {
                NAV: REIT.NAV,
                NAVCount: REIT.NAVCount,
              }
            : undefined,
        dividendDiscount:
          dividend_discount !== null
            ? {
                DDMGrowth: dividend_discount.ddm_growth,
                DDMSource: dividend_discount.ddm_source,
                DPS: dividend_discount.dps,
                payout: dividend_discount.payout,
                ROE: dividend_discount.roe,
                expectedGrowth: dividend_discount.expected_growth,
              }
            : undefined,
        excessReturns:
          excess_returns !== null
            ? {
                stableBookValue: excess_returns.stable_book_value,
                returnOnEquityAvg: excess_returns.return_on_equity_avg,
                stableEPS: excess_returns.stable_eps,
                stableEPSSource: excess_returns.stable_eps_source,
                stableBookValueSource: excess_returns.stable_book_value_source,
                bookValue: excess_returns.book_value,
                equityCost: excess_returns.equity_cost,
                excessReturn: excess_returns.excess_return,
              }
            : undefined,
      };
    },
  });
}
